// Featured-posts slider on the blog page (blocks/_blog-featured-post-group).
// Mirrors the slideshow section's switch effects (slide / fade / creative zoom)
// and its cursor-following navigation; lifecycle-safe for the theme editor.
(function () {
  const initCursorNavigation = (root, sliderEl, swiper) => {
    const cursorEl = root.querySelector(".js-blog-featured-cursor");
    if (!cursorEl) return null;

    // Read off the tracked element rather than the document, as the shared
    // SliderDragCursor does: it is the direction Swiper itself resolves.
    let isRtl = getComputedStyle(sliderEl).direction === "rtl";

    // In RTL the slides read right to left, so the near half is the NEXT one —
    // the shared cursor CSS flips the glyphs on that assumption.
    const isPrevHalf = (x, width) => (isRtl ? x > width / 2 : x < width / 2);

    const onMove = (event) => {
      const rect = sliderEl.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      cursorEl.style.setProperty("--cursor-x", `${x}px`);
      cursorEl.style.setProperty("--cursor-y", `${y}px`);
      const isPrev = isPrevHalf(x, rect.width);
      cursorEl.classList.toggle("slider-drag-cursor--prev", isPrev);
      cursorEl.classList.toggle("slider-drag-cursor--next", !isPrev);
    };

    const onEnter = () => {
      isRtl = getComputedStyle(sliderEl).direction === "rtl";
      cursorEl.classList.add("is-visible");
    };
    const onLeave = () => cursorEl.classList.remove("is-visible");

    const onClick = (event) => {
      // Ignore clicks on interactive content (title link, tag chips, controls).
      if (
        event.target.closest("a, button, .button, input, .slider-nav-button")
      ) {
        return;
      }
      const rect = sliderEl.getBoundingClientRect();
      const isPrev = isPrevHalf(event.clientX - rect.left, rect.width);

      cursorEl.classList.add("is-clicking");
      cursorEl.addEventListener(
        "animationend",
        () => cursorEl.classList.remove("is-clicking"),
        { once: true }
      );

      if (isPrev) swiper.slidePrev();
      else swiper.slideNext();
    };

    sliderEl.addEventListener("pointermove", onMove);
    sliderEl.addEventListener("pointerenter", onEnter);
    sliderEl.addEventListener("pointerleave", onLeave);
    sliderEl.addEventListener("click", onClick);

    return () => {
      sliderEl.removeEventListener("pointermove", onMove);
      sliderEl.removeEventListener("pointerenter", onEnter);
      sliderEl.removeEventListener("pointerleave", onLeave);
      sliderEl.removeEventListener("click", onClick);
    };
  };

  // Creative stacks the slides, so Swiper's own drag stays off (there is nothing
  // to follow the finger with) — but the gesture still has to switch them: with
  // arrows, dots and cursor navigation all disabled it is the only way to advance.
  // Threshold in px; a shorter travel stays a click.
  const initSwipeGesture = (sliderEl, swiper) => {
    const threshold = 40;
    let pointerId = null;
    let startX = 0;
    let startY = 0;

    const onDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
    };

    const onUp = (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      pointerId = null;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      // A mostly-vertical drag is a page scroll, not a slide switch.
      if (Math.abs(deltaX) < threshold || Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }
      const isRtl = getComputedStyle(sliderEl).direction === "rtl";
      const goNext = isRtl ? deltaX > 0 : deltaX < 0;
      if (goNext) swiper.slideNext();
      else swiper.slidePrev();
    };

    const onCancel = () => {
      pointerId = null;
    };

    sliderEl.addEventListener("pointerdown", onDown);
    sliderEl.addEventListener("pointerup", onUp);
    sliderEl.addEventListener("pointercancel", onCancel);

    return () => {
      sliderEl.removeEventListener("pointerdown", onDown);
      sliderEl.removeEventListener("pointerup", onUp);
      sliderEl.removeEventListener("pointercancel", onCancel);
    };
  };

  const initSlider = (sliderEl) => {
    if (!sliderEl || typeof Swiper === "undefined") return;

    // The editor re-runs the inline script AND fires shopify:section:load —
    // bail on the second call (already a live Swiper on this element).
    if (sliderEl.swiper) return;

    const root = sliderEl.closest(".blog-featured");
    if (!root) return;

    const animationType = sliderEl.dataset.animationType || "slide";
    const cursorNavigation =
      sliderEl.getAttribute("data-cursor-navigation") === "true";
    const sliderLoop = sliderEl.getAttribute("data-loop") === "true";
    const hasAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
    const pauseAutoplayOnHover =
      sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
    const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 5000);
    const hasParallax = sliderEl.getAttribute("data-parallax") === "true";

    const paginationEl = root.querySelector(".slider-pagination");
    const nextBtn = root.querySelector(".slider-nav-button--next");
    const prevBtn = root.querySelector(".slider-nav-button--prev");

    // Only an autoplaying slider keeps the played bullets filled (the row then reads
    // as a progress track); without autoplay the fill marks the active bullet alone.
    if (paginationEl) {
      paginationEl.classList.toggle("slider-pagination--autoplay", hasAutoplay);
    }

    // Parallax moves whatever carries the attribute, and a nested slide can't read
    // the group's setting — so the flag arrives on the slider and is applied here,
    // to the copy column, before Swiper reads the markup.
    const parallaxTargets = hasParallax
      ? sliderEl.querySelectorAll(".blog-featured__layout")
      : [];
    parallaxTargets.forEach((layout) =>
      layout.setAttribute("data-swiper-parallax", "-700")
    );

    // Deterministic creative stacking (active on top, the rest below) — a fast
    // interrupt can't leave the wrong slide on top; also clears a stuck zoom.
    const setCreativeZ = (swiper) => {
      swiper.slides.forEach((slide, i) => {
        slide.classList.remove("is-zooming");
        slide.style.zIndex = i === swiper.activeIndex ? "2" : "1";
      });
    };

    // Slides off the stage keep their links in the document: without this Tab
    // walks through headlines and tag chips nobody can see. inert takes them out
    // of the tab order and off the accessibility tree in one attribute.
    const syncSlidesAccessibility = (swiper) => {
      // By index rather than the active class: this runs on slideChange, before
      // Swiper has necessarily rewritten the classes.
      const activeSlide = swiper.slides[swiper.activeIndex];
      swiper.slides.forEach((slide) => {
        const isActive = slide === activeSlide;
        slide.toggleAttribute("inert", !isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      });
    };

    const swiperInstance = new Swiper(sliderEl, {
      loop: sliderLoop,
      slidesPerView: 1,
      spaceBetween: animationType === "slide" ? 12 : 0,
      speed: animationType === "creative" ? 1000 : 800,
      watchSlidesProgress: true,
      // The creative zoom advances on click/cursor-nav/pagination; drag-follow
      // makes no sense for stacked slides, so touch-move is off for it.
      allowTouchMove: animationType !== "creative",
      effect: animationType,
      fadeEffect: { crossFade: true },
      creativeEffect:
        animationType === "creative"
          ? {
              // Slides stay stacked; the incoming image grows via CSS
              // (.is-zooming). The ~imperceptible 0.999 opacity is load-bearing:
              // a pure [0,0,0]/opacity:1 fires no transitionend and Swiper locks.
              prev: { translate: [0, 0, 0], opacity: 0.999 },
              next: { translate: [0, 0, 0], opacity: 0.999 },
            }
          : {},
      resistanceRatio: animationType === "creative" ? 0 : 0.85,
      parallax: hasParallax,
      // Trackpad two-finger swipe: the only way to advance when the creative
      // effect turns drag off and neither arrows nor pagination are shown.
      mousewheel: {
        forceToAxis: true,
      },
      // Slides ship lazy (the group makes the first one eager): the neighbour is
      // fetched ahead of the switch so it never arrives mid-transition.
      lazyPreloadPrevNext: 1,
      autoplay: hasAutoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: pauseAutoplayOnHover,
          }
        : false,
      pagination: {
        el: paginationEl,
        clickable: true,
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
        disabledClass: "swiper-button-disabled",
      },
      breakpoints: {
        576: {
          spaceBetween: animationType === "slide" ? 20 : 0,
        },
      },
      on: {
        afterInit(swiper) {
          syncSlidesAccessibility(swiper);
        },
        autoplayTimeLeft(s, time, progress) {
          if (paginationEl) {
            paginationEl.style.setProperty("--slider-progress", 1 - progress);
          }
        },
        slideChange(swiper) {
          syncSlidesAccessibility(swiper);
        },
        slideChangeTransitionStart() {
          if (animationType !== "creative") return;
          // Lock advancing for the duration of the zoom so a rapid click can't
          // restart it mid-grow, then lift the incoming slide above all and the
          // outgoing one just beneath it.
          this.allowSlideNext = false;
          this.allowSlidePrev = false;

          setCreativeZ(this);
          const topZ = this.slides.length + 2;
          const activeSlide = this.slides[this.activeIndex];
          const prevSlide = this.slides[this.previousIndex];
          if (activeSlide) {
            activeSlide.classList.add("is-zooming");
            activeSlide.style.zIndex = topZ;
          }
          if (prevSlide && prevSlide !== activeSlide) {
            prevSlide.style.zIndex = topZ - 1;
          }
        },
        slideChangeTransitionEnd() {
          if (animationType !== "creative") return;
          this.allowSlideNext = true;
          this.allowSlidePrev = true;
          setCreativeZ(this);
        },
      },
    });

    const cursorCleanup = cursorNavigation
      ? initCursorNavigation(root, sliderEl, swiperInstance)
      : null;

    const swipeCleanup =
      animationType === "creative"
        ? initSwipeGesture(sliderEl, swiperInstance)
        : null;

    // Pause autoplay while the hero is off-screen.
    let observer = null;
    if (hasAutoplay && swiperInstance.autoplay) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) swiperInstance.autoplay.resume();
          else swiperInstance.autoplay.pause();
        });
      });
      observer.observe(root);
    }

    sliderEl._blogFeaturedCleanup = () => {
      if (observer) observer.disconnect();
      if (typeof cursorCleanup === "function") cursorCleanup();
      if (typeof swipeCleanup === "function") swipeCleanup();
      if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);
      // destroy() drops Swiper's own classes only, so what we added is ours to
      // clear — a re-init on a stale inert slide would stay unfocusable, and a
      // parallax offset left behind would hold the copy off-centre.
      sliderEl.querySelectorAll(".blog-featured__slide").forEach((slide) => {
        slide.removeAttribute("inert");
        slide.removeAttribute("aria-hidden");
      });
      parallaxTargets.forEach((layout) => {
        layout.removeAttribute("data-swiper-parallax");
        layout.style.transform = "";
      });
      sliderEl._blogFeaturedCleanup = null;
    };
  };

  const initSection = (scope) => {
    if (!scope || typeof scope.querySelectorAll !== "function") return;
    scope.querySelectorAll(".js-blog-featured-swiper").forEach(initSlider);
  };

  initSection(document);

  if (!window.__blogFeaturedSliderEvents) {
    window.__blogFeaturedSliderEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      if (!event.target || typeof event.target.querySelectorAll !== "function")
        return;
      event.target
        .querySelectorAll(".js-blog-featured-swiper")
        .forEach((el) => {
          if (typeof el._blogFeaturedCleanup === "function") {
            el._blogFeaturedCleanup();
          }
        });
    });

    // Theme editor: jump to the slide of the block being edited.
    document.addEventListener("shopify:block:select", (event) => {
      const slide = event.target.closest?.(".blog-featured__slide");
      const sliderEl = event.target.closest?.(".js-blog-featured-swiper");
      const swiper = sliderEl && sliderEl.swiper;
      if (!slide || !swiper || !slide.parentElement) return;
      const index = Array.from(slide.parentElement.children).indexOf(slide);
      if (index >= 0) {
        if (swiper.params.loop) swiper.slideToLoop(index);
        else swiper.slideTo(index);
      }
    });
  }
})();
